import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Bot, Clock, FileSearch, ListChecks, Loader2, Sparkles, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface AITopMatchesLauncherProps {
	initialInstructions: string;
	initialExcludeStatuses: string[];
	starting: boolean;
	error?: string | null;
	onStart: (instructions: string, excludeStatuses: string[]) => void;
	onBack?: () => void;
}

const MAX_INSTRUCTIONS = 2000;

export function AITopMatchesLauncher({ initialInstructions, initialExcludeStatuses, starting, error, onStart, onBack }: AITopMatchesLauncherProps) {
	const { t } = useTranslation();
	const [instructions, setInstructions] = useState(initialInstructions);
	const [excludeHired, setExcludeHired] = useState(initialExcludeStatuses.includes("hired"));
	const [excludeUnavailable, setExcludeUnavailable] = useState(initialExcludeStatuses.includes("unavailable"));

	const steps = [
		{ icon: Target, title: t("aiTopMatches.launcher.steps.plan"), description: t("aiTopMatches.launcher.steps.planDescription") },
		{ icon: ListChecks, title: t("aiTopMatches.launcher.steps.screen"), description: t("aiTopMatches.launcher.steps.screenDescription") },
		{ icon: Bot, title: t("aiTopMatches.launcher.steps.scout"), description: t("aiTopMatches.launcher.steps.scoutDescription") },
		{ icon: FileSearch, title: t("aiTopMatches.launcher.steps.deep"), description: t("aiTopMatches.launcher.steps.deepDescription") },
	];

	const handleStart = () => {
		const excludeStatuses = [
			...(excludeHired ? ["hired"] : []),
			...(excludeUnavailable ? ["unavailable"] : []),
		];
		onStart(instructions.trim(), excludeStatuses);
	};

	return (
		<>
			<div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
				{error && (
					<div className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 p-3 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
						<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
						<span>{error}</span>
					</div>
				)}

				<div>
					<p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("aiTopMatches.launcher.howItWorks")}</p>
					<ol className="grid gap-px overflow-hidden rounded-sm border border-border bg-border sm:grid-cols-4">
						{steps.map(({ icon: Icon, title, description }, index) => (
							<li key={title} className="flex flex-col gap-1.5 bg-background p-3">
								<div className="flex items-center gap-2">
									<span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300">
										<Icon className="h-3.5 w-3.5" />
									</span>
									<span className="text-xs font-semibold text-foreground">{index + 1}. {title}</span>
								</div>
								<p className="text-[11px] leading-snug text-muted-foreground">{description}</p>
							</li>
						))}
					</ol>
				</div>

				<div className="space-y-1.5">
					<Label htmlFor="ai-top-matches-instructions" className="text-sm font-medium">{t("aiTopMatches.launcher.instructionsLabel")}</Label>
					<Textarea
						id="ai-top-matches-instructions"
						value={instructions}
						onChange={(e) => setInstructions(e.target.value.slice(0, MAX_INSTRUCTIONS))}
						placeholder={t("aiTopMatches.launcher.instructionsPlaceholder")}
						rows={4}
						className="resize-none rounded-sm text-sm"
					/>
					<div className="flex justify-between text-[11px] text-muted-foreground">
						<span>{t("aiTopMatches.launcher.instructionsHint")}</span>
						<span>{instructions.length}/{MAX_INSTRUCTIONS}</span>
					</div>
				</div>

				<div className="flex flex-wrap gap-x-6 gap-y-2 rounded-sm border border-border bg-muted/30 px-3 py-2.5">
					<div className="flex items-center gap-2">
						<Checkbox id="ai-top-matches-exclude-hired" checked={excludeHired} onCheckedChange={(checked) => setExcludeHired(checked === true)} />
						<Label htmlFor="ai-top-matches-exclude-hired" className="cursor-pointer text-xs font-normal">{t("aiTopMatches.launcher.excludeHired")}</Label>
					</div>
					<div className="flex items-center gap-2">
						<Checkbox id="ai-top-matches-exclude-unavailable" checked={excludeUnavailable} onCheckedChange={(checked) => setExcludeUnavailable(checked === true)} />
						<Label htmlFor="ai-top-matches-exclude-unavailable" className="cursor-pointer text-xs font-normal">{t("aiTopMatches.launcher.excludeUnavailable")}</Label>
					</div>
				</div>
			</div>

			<div className="flex shrink-0 items-center justify-between gap-3 border-t border-border bg-background px-5 py-3">
				<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<Clock className="h-3.5 w-3.5" />
					{t("aiTopMatches.launcher.estimate")}
				</span>
				<div className="flex gap-2">
					{onBack && (
						<Button variant="outline" size="sm" onClick={onBack} disabled={starting}>
							{t("aiTopMatches.launcher.backToResults")}
						</Button>
					)}
					<Button size="sm" onClick={handleStart} disabled={starting} className="bg-purple-600 hover:bg-purple-700">
						{starting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
						{starting ? t("aiTopMatches.launcher.starting") : t("aiTopMatches.launcher.start")}
					</Button>
				</div>
			</div>
		</>
	);
}
